type DeepKeyOf<T> = T extends object
  ? {
      [K in keyof T]-?: K extends string
        ? | K
        | (T[K] extends Record<string, any>
          ? `${K}.${DeepKeyOf<T[K]>}`
          : never)
        : | K
          | (T[K] extends Record<string, any>
            ? `${K & number}.${DeepKeyOf<T[K]>}`
            : never);
    }[keyof T]
  : never

type DeepValueOf<
  T,
  K extends DeepKeyOf<T>,
> = K extends `${infer First}.${infer Rest}`
  ? First extends keyof T
    ? Rest extends DeepKeyOf<T[First]>
      ? DeepValueOf<T[First], Rest>
      : never
    : never
  : K extends keyof T
    ? T[K]
    : never

export enum ErrorCode {
  NO_ERROR = 0,
  GENERAL_EXCEPTION = 101,
  GENERAL_INITIALIZATION_FAILURE = 102,
  ALREADY_INITIALIZED = 103,
  CONTENT_INSTANCE_TERMINATED = 104,
  GENERAL_TERMINATION_FAILURE = 111,
  TERMINATE_BEFORE_INIT = 112,
  TERMINATE_AFTER_TERMINATE = 113,
  GET_BEFORE_INIT = 122,
  GET_AFTER_TERMINATE = 123,
  SET_BEFORE_INIT = 132,
  SET_AFTER_TERMINATE = 133,
  COMMIT_BEFORE_INIT = 142,
  COMMIT_AFTER_TERMINATE = 143,
  GENERAL_ARGUMENT_ERROR = 201,
  GENERAL_GET_FAILURE = 301,
  GENERAL_SET_FAILURE = 351,
  GENERAL_COMMIT_FAILURE = 391,
  UNDEFINED_DATA_MODEL_ELEMENT = 401,
  UNIMPLEMENTED_DATA_MODEL_ELEMENT = 402,
  DATA_MODEL_ELEMENT_VALUE_NOT_INITIALIZED = 403,
  DATA_MODEL_ELEMENT_IS_READ_ONLY = 404,
  DATA_MODEL_ELEMENT_IS_WRITE_ONLY = 405,
  DATA_MODEL_ELEMENT_TYPE_MISMATCH = 406,
  DATA_MODEL_ELEMENT_VALUE_OUT_OF_RANGE = 407,
  DATA_MODEL_DEPENDENCY_NOT_ESTABLISHED = 408,
}

export type TScorm2004 = {
  Initialize: () => boolean
  Terminate: () => boolean
  GetValue: <K extends DeepKeyOf<CMI>>(key: K) => DeepValueOf<CMI, K> | never
  SetValue: <K extends DeepKeyOf<CMI>>(
    key: K,
    value: DeepValueOf<CMI, K> | string | number,
  ) => boolean
  Commit: () => boolean
  GetLastError: () => ErrorCode
  GetErrorString: (errorCode: ErrorCode) => string
  GetDiagnostic: (errorCode: ErrorCode) => string
}

export type CMI = {
  cmi: {
    _version: string
    comments_from_learner: {
      [key: number]: {
        comment: string
        location: string
        timestamp: string
      }
      _children: string
      _count: number
    }
    comments_from_lms: {
      [key: number]: {
        comment: string
        location: string
        timestamp: string
      }
      _children: string
      _count: number
    }
    completion_status: 'completed' | 'incomplete' | 'not attempted' | 'unknown'
    // completion_threshold: number;
    credit: 'credit' | 'no-credit'
    entry: 'ab-initio' | 'resume'
    exit: 'time-out' | 'suspend' | 'logout' | 'normal'
    interactions: {
      [key: number]: {
        id: number
        type:
          | 'true-false'
          | 'choice'
          | 'fill-in'
          | 'long-fill-in'
          | 'matching'
          | 'performance'
          | 'sequencing'
          | 'likert'
          | 'numeric'
          | 'other'
        objectives: {
          [key: number]: {
            id: number
          }
          _count: number
        }
        correct_responses: {
          [key: number]: {
            pattern: string
          }
          _count: number
        }
        weighting: number
        learner_response: string
        result: 'correct' | 'incorrect' | 'unanticipated' | 'neutral'
        latency: string
        description: string
        timestamp: string
      }
      _children: string
      _count: number
    }
    launch_data: string
    learner_id: string
    learner_name: string
    learner_preference: {
      _children: 'audio_level,language,delivery_speed,audio_captioning'
      audio_level: number
      language: string
      delivery_speed: number
      audio_captioning: '-1' | '0' | '1'
    }
    location: string
    max_time_allowed: string
    mode: 'browse' | 'normal' | 'review'
    objectives: {
      [key: number]: {
        id: number
        score: {
          _children: 'scaled,raw,min,max'
          scaled: number
          raw: number
          min: number
          max: number
        }
        success_status: 'passed' | 'failed' | 'unknown'
        completion_status:
          | 'completed'
          | 'incomplete'
          | 'not attempted'
          | 'unknown'
        progress_measure: number
        description: string
      }
      _children: 'progress_measure,description,score,id,success_status,completion_status'
      _count: number
    }
    progress_measure: number
    scaled_passing_score: number
    score: {
      _children: 'scaled,raw,min,max'
      scaled: number
      raw: number
      min: number
      max: number
    }
    session_time: string
    success_status: 'passed' | 'failed' | 'unknown'
    suspend_data: string
    time_limit_action:
      | 'exit,message'
      | 'continue,message'
      | 'exit,no message'
      | 'continue,no message'
    total_time: string
  }
  adl: {
    nav: {
      request: string
      request_valid: {
        previous: boolean
        continue: boolean
        // choice: boolean;
      }
    }
  }
}
/*
export const API_1484_11: TScorm2004 = window.API_1484_11;

API_1484_11.GetValue('cmi.comments_from_learner.1.comment');
API_1484_11.SetValue('cmi.score.max', '10');
API_1484_11.GetErrorString(101); */
